import { supabase } from '../lib/supabaseClient';
import type {
  QuestionComment,
  CreateQuestionCommentInput,
  UpdateQuestionCommentStatusInput,
  UpdateQuestionCommentBodyInput,
  UpdateQuestionCommentModerationInput,
} from '../types/questionComments';

/**
 * 質問に対するコメントを取得
 * @param questionId 質問ID
 * @param options オプション
 * @param options.includeUnpublished 未公開コメントも含めるか（管理者用）
 */
export async function fetchQuestionComments(
  questionId: string,
  options?: { includeUnpublished?: boolean }
): Promise<QuestionComment[]> {
  let query = supabase
    .from('question_comments')
    .select('*')
    .eq('question_id', questionId);

  // 公開側：is_published = TRUE のコメントのみ
  if (!options?.includeUnpublished) {
    query = query.eq('is_published', true);
  }

  // 時系列順（古い順）で並べる
  query = query.order('created_at', { ascending: true });

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching question comments:', error);
    throw error;
  }

  return data || [];
}

/**
 * 承認待ちのコメントを取得（管理用）
 * @param limit 取得件数
 */
export async function fetchPendingComments(limit = 50): Promise<QuestionComment[]> {
  const { data, error } = await supabase
    .from('question_comments')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching pending comments:', error);
    throw error;
  }

  return data || [];
}

/**
 * すべてのコメントを取得（管理用）
 * @param limit 取得件数
 */
export async function fetchAllCommentsForAdmin(limit = 100): Promise<QuestionComment[]> {
  const { data, error } = await supabase
    .from('question_comments')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching all comments:', error);
    throw error;
  }

  return data || [];
}

/**
 * コメントIDで1件取得（管理用）
 * @param id コメントID
 */
export async function fetchQuestionCommentById(id: string): Promise<QuestionComment | null> {
  const { data, error } = await supabase
    .from('question_comments')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching question comment by id:', error);
    throw error;
  }

  return data;
}

/**
 * 新規コメントを投稿
 * @param input コメント投稿データ
 */
export async function createQuestionComment(
  input: CreateQuestionCommentInput
): Promise<QuestionComment> {
  const { data, error } = await supabase
    .from('question_comments')
    .insert({
      question_id: input.question_id,
      author_name: input.author_name,
      author_role: input.author_role || null,
      body_raw: input.body_raw,
      body_public: input.body_raw, // 最初は同じ内容を保存
      status: 'pending', // 承認待ち
      is_published: false, // 未公開
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating question comment:', error);
    throw error;
  }

  return data;
}

/**
 * コメントのステータスと公開フラグを更新（管理用）
 * @param id コメントID
 * @param input 更新データ
 */
export async function updateQuestionCommentStatus(
  id: string,
  input: UpdateQuestionCommentStatusInput
): Promise<QuestionComment> {
  const { data, error } = await supabase
    .from('question_comments')
    .update({
      status: input.status,
      is_published: input.is_published,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating question comment status:', error);
    throw error;
  }

  return data;
}

/**
 * コメントの公開用本文を更新（管理用）
 * @param id コメントID
 * @param input 更新データ
 */
export async function updateQuestionCommentBody(
  id: string,
  input: UpdateQuestionCommentBodyInput
): Promise<QuestionComment> {
  const { data, error } = await supabase
    .from('question_comments')
    .update({
      body_public: input.body_public,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateQuestionCommentModeration(
  id: string,
  input: UpdateQuestionCommentModerationInput
): Promise<QuestionComment> {
  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  if (typeof input.status !== 'undefined') {
    updateData.status = input.status;
  }
  if (typeof input.is_published !== 'undefined') {
    updateData.is_published = input.is_published;
  }
  if (typeof input.body_public !== 'undefined') {
    updateData.body_public = input.body_public;
  }

  const { data, error } = await supabase
    .from('question_comments')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * コメントを削除（管理用）
 * @param id コメントID
 */
export async function deleteQuestionComment(id: string): Promise<void> {
  const { error } = await supabase
    .from('question_comments')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting question comment:', error);
    throw error;
  }
}
